import ValidatorFormGroupDropDown from './ValidatorFormGroupDropDown';
import {
    OperationalContactRoles,
    OperationalContactSalutation,
    OperationalContactWriteDto,
} from '../../store/operationalContactsSlice';
import { FormControl } from 'react-bootstrap';
import React, { CSSProperties } from 'react';
import { useFormikContext } from 'formik';
import ValidatorFormGroup from '../main/validator-form-group';
import useTranslate from '../useTranslate';
import { FormikErrors } from 'formik/dist/types';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

interface Props {
    active: boolean;
    disabled?: boolean;
    defaultCountry?: string;
}

export function filterOperationalContactValues(
    input: OperationalContactWriteDto
): OperationalContactWriteDto {
    return {
        ...input,
        phone1: isEmptyPhoneNumber(input.phone1) ? '' : input.phone1,
        phone2: isEmptyPhoneNumber(input.phone2) ? '' : input.phone2,
    };
}

function isEmptyPhoneNumber(input: string): boolean {
    if (!input) return true;
    const trimmed = input.trim();
    if (trimmed.startsWith('+') && trimmed.length < 5) {
        return true;
    }
    return trimmed.length === 0;
}

function phoneNumberE1640OrNull(input: string): string | null {
    const raw = input.trim();
    if (!raw) return null;

    if (/^\+\d{7,20}$/.test(raw.replaceAll(/\s/g, ''))) {
        return raw.replaceAll(/\s/g, '');
    }

    return null;
}

export function validateOperationalContact(
    values: OperationalContactWriteDto
): FormikErrors<OperationalContactWriteDto> {
    const errors: FormikErrors<OperationalContactWriteDto> = {};

    if (!values.name || values.name.length < 2) {
        errors.name = 'NAME_MISSING';
    }

    const p1 = phoneNumberE1640OrNull(values.phone1);
    const p2 = phoneNumberE1640OrNull(values.phone2);

    if (!values.email && !p1 && !p2) {
        errors.email = 'MISSING_CONTACT_INFORMATION';
        errors.phone1 = 'MISSING_CONTACT_INFORMATION';
        errors.phone2 = 'MISSING_CONTACT_INFORMATION';
    } else {
        if (!isEmptyPhoneNumber(values.phone1) && !p1) {
            errors.phone1 = 'INVALID_PHONE_NUMBER';
        }
        if (!isEmptyPhoneNumber(values.phone2) && !p2) {
            errors.phone2 = 'INVALID_PHONE_NUMBER';
        }
    }

    return errors;
}

function OperationalContactBasicForm(props: Readonly<Props>) {
    const { defaultCountry = 'de' } = props;
    const formikProps = useFormikContext<OperationalContactWriteDto>();
    const strings = useTranslate('OperationalContactEdit');
    const salutationStrings = useTranslate('OperationalContactSalutationNames');
    const roleStrings = useTranslate('OperationalContactRoleNames');
    const phoneDisplay: CSSProperties = props.active
        ? { display: 'none' }
        : undefined;
    const phoneEdit: CSSProperties = props.active
        ? undefined
        : { display: 'none' };

    return (
        <div>
            <ValidatorFormGroupDropDown
                disabled={props.disabled}
                label={strings.labelSalutation}
                values={OperationalContactSalutation}
                strings={salutationStrings}
                name={'salutation'}
                value={formikProps.values.salutation}
                onChange={formikProps.handleChange}
            />
            <ValidatorFormGroup label={strings.labelFirstName}>
                <FormControl
                    disabled={props.disabled}
                    type={'text'}
                    onChange={formikProps.handleChange}
                    name={'first_name'}
                    value={formikProps.values.first_name}
                />
            </ValidatorFormGroup>
            <ValidatorFormGroup
                label={strings.labelName}
                hideErrorLabel={!formikProps.errors.name}
                errorLabel={strings.errorName}
            >
                <FormControl
                    disabled={props.disabled}
                    type={'text'}
                    onChange={formikProps.handleChange}
                    name={'name'}
                    value={formikProps.values.name}
                />
            </ValidatorFormGroup>
            <ValidatorFormGroup
                label={strings.labelEmail}
                hideErrorLabel={!formikProps.errors.email}
                errorLabel={strings.errorContactEmailOrPhone}
            >
                <FormControl
                    disabled={props.disabled}
                    type={'text'}
                    onChange={formikProps.handleChange}
                    name={'email'}
                    value={formikProps.values.email}
                />
            </ValidatorFormGroup>
            <ValidatorFormGroup
                label={strings.labelPhone1}
                hideErrorLabel={!formikProps.errors.phone1}
                errorLabel={strings.errorContactEmailOrPhone}
            >
                <FormControl
                    style={phoneDisplay}
                    disabled
                    type={'text'}
                    name={'phone1'}
                    value={
                        isEmptyPhoneNumber(formikProps.values.phone1)
                            ? ''
                            : formikProps.values.phone1
                    }
                />
                <PhoneInput
                    style={phoneEdit}
                    disabled={props.disabled}
                    onChange={(value) =>
                        formikProps.setFieldValue('phone1', value)
                    }
                    value={formikProps.values.phone1}
                    defaultCountry={defaultCountry.toLowerCase()}
                    inputProps={{ className: 'form-control' }}
                    inputClassName={'form-control'}
                />
            </ValidatorFormGroup>
            <ValidatorFormGroup
                label={strings.labelPhone2}
                hideErrorLabel={!formikProps.errors.phone2}
                errorLabel={strings.errorContactEmailOrPhone}
            >
                <FormControl
                    style={phoneDisplay}
                    disabled
                    type={'text'}
                    name={'phone2'}
                    value={
                        isEmptyPhoneNumber(formikProps.values.phone2)
                            ? ''
                            : formikProps.values.phone2
                    }
                />
                <PhoneInput
                    style={phoneEdit}
                    disabled={props.disabled}
                    onChange={(value) =>
                        formikProps.setFieldValue('phone2', value)
                    }
                    value={formikProps.values.phone2}
                    defaultCountry={defaultCountry.toLowerCase()}
                    inputProps={{ className: 'form-control' }}
                    inputClassName={'form-control'}
                />
            </ValidatorFormGroup>
            <ValidatorFormGroupDropDown
                disabled={props.disabled}
                label={strings.labelRole}
                values={OperationalContactRoles}
                strings={roleStrings}
                name={'role'}
                value={formikProps.values.role}
                onChange={formikProps.handleChange}
            />
        </div>
    );
}

export default OperationalContactBasicForm;
